/**
 * IndustryBenchmarkCard — Dashboard card wrapper for IndustryBenchmark
 *
 * Fetches the selected case's financial metrics from the API
 * and feeds them into the existing IndustryBenchmark visualization.
 * Shows loading/empty states when no case is selected.
 */

import React, { useState, useEffect } from 'react';
import { BarChart3, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';
import IndustryBenchmark from '../IndustryBenchmark';

interface IndustryBenchmarkCardProps {
  reportId?: string;
  className?: string;
  /**
   * Server-computed ratios from /dashboard/case-details. When present, the
   * widget renders from this slice instead of hitting /api/v1/reports/{id}
   * and computing ratios client-side. The JS math now lives in
   * backend dashboard_api._compute_industry_benchmark.
   */
  data?: {
    available?: boolean;
    reason?: string;
    business_type?: string;
    naics_code?: string;
    cogs_ratio?: number;
    gross_margin?: number;
    operating_expense_ratio?: number;
    net_profit_margin?: number;
    has_reconstructed_data?: boolean;
    error?: string;
  } | null;
  isLoading?: boolean;
}

interface FinancialMetrics {
  businessType?: string;
  naicsCode?: string;
  cogsRatio: number;
  grossMargin: number;
  operatingExpenseRatio: number;
  netProfitMargin: number;
  hasReconstructedData: boolean;
}

export default function IndustryBenchmarkCard({
  reportId,
  className = '',
  data: sliceData,
  isLoading: externalLoading,
}: IndustryBenchmarkCardProps) {
  const [metrics, setMetrics] = useState<FinancialMetrics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const batchedMode = sliceData !== undefined;

  // Derive metrics from the slice in batched mode; otherwise keep the
  // original /api/v1/reports/{id} fetch path (still used by any legacy
  // consumer that passes only reportId).
  useEffect(() => {
    if (!batchedMode) return;
    if (!sliceData) {
      setMetrics(null);
      setError(null);
      return;
    }
    if (sliceData.error) {
      setMetrics(null);
      setError('Failed to load benchmark data');
      return;
    }
    if (sliceData.available === false) {
      setMetrics(null);
      setError(sliceData.reason || 'No financial data available for benchmark comparison');
      return;
    }
    setError(null);
    setMetrics({
      businessType: sliceData.business_type,
      naicsCode: sliceData.naics_code,
      cogsRatio: sliceData.cogs_ratio ?? 0,
      grossMargin: sliceData.gross_margin ?? 0,
      operatingExpenseRatio: sliceData.operating_expense_ratio ?? 0,
      netProfitMargin: sliceData.net_profit_margin ?? 0,
      hasReconstructedData: !!sliceData.has_reconstructed_data,
    });
  }, [batchedMode, sliceData]);

  useEffect(() => {
    if (batchedMode) return; // parent drives state
    if (!reportId) {
      setMetrics(null);
      return;
    }
    fetchMetrics(reportId);
  }, [reportId, batchedMode]);

  const fetchMetrics = async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const report = await api.get(`/api/v1/reports/${id}`);

      const financialData = report.financial_data || {};
      const businessType = report.business_type || report.data?.business_type || '';

      // Calculate financial ratios from report data
      let totalRevenue = 0;
      let totalCogs = 0;
      let totalExpenses = 0;
      let totalNetProfit = 0;
      let monthCount = 0;

      // Try canonical_data first (granular P&L)
      const canonical = report.data?.canonical_data || report.canonical_data;
      if (canonical?.periods) {
        for (const period of canonical.periods) {
          const rev = period.revenue || 0;
          const cogs = period.cogs || period.cost_of_goods_sold || 0;
          const exp = period.total_expenses || period.expenses || 0;
          const net = period.net_income || period.net_profit || (rev - exp);
          totalRevenue += rev;
          totalCogs += cogs;
          totalExpenses += exp;
          totalNetProfit += net;
          monthCount++;
        }
      }

      // Fallback to financial_data year arrays
      if (monthCount === 0) {
        for (const key of Object.keys(financialData)) {
          if (!key.startsWith('data_') || !Array.isArray(financialData[key])) continue;
          for (const m of financialData[key]) {
            if (!m || typeof m !== 'object') continue;
            const rev = m.revenue || 0;
            const cogs = m.cogs || 0;
            const payroll = typeof m.payroll === 'object' ? (m.payroll.total || 0) : (m.payroll || 0);
            const expenses = Object.values(m.expenses || {}).reduce((a: number, b: any) => a + (Number(b) || 0), 0);
            totalRevenue += rev;
            totalCogs += cogs;
            totalExpenses += cogs + payroll + expenses;
            totalNetProfit += rev - (cogs + payroll + expenses);
            monthCount++;
          }
        }
      }

      // NOTE: A previous fallback synthesized COGS as `benchmark_period.revenue *
      // 0.35` (and expenses/net from the variable margin) when no granular P&L was
      // available. That fabricated the COGS ratio and gross margin — the two core
      // benchmark comparisons — from a hardcoded ratio. Per CLAIMGENIUS rule #3
      // (code calculates, frontend renders), we no longer fabricate COGS. When the
      // canonical/financial P&L data needed for these ratios is absent, we show the
      // honest "no data available" state below rather than a fabricated benchmark.

      if (totalRevenue > 0) {
        setMetrics({
          businessType,
          naicsCode: report.naics_code,
          cogsRatio: (totalCogs / totalRevenue) * 100,
          grossMargin: ((totalRevenue - totalCogs) / totalRevenue) * 100,
          operatingExpenseRatio: (totalExpenses / totalRevenue) * 100,
          netProfitMargin: (totalNetProfit / totalRevenue) * 100,
          hasReconstructedData: report.data?.reconstructed === true || report.fromBulkUpload === true,
        });
      } else {
        setMetrics(null);
        setError('No financial data available for benchmark comparison');
      }
    } catch (err: any) {
      console.error('Failed to fetch benchmark metrics:', err);
      setError('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  // No case selected
  if (!reportId) {
    return (
      <div
        className={`rounded-xl border shadow-sm p-6 ${className}`}
        style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Industry Benchmarks</h3>
        </div>
        <div className="text-center py-8">
          <BarChart3 className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Select a case to view industry benchmark comparison</p>
        </div>
      </div>
    );
  }

  // Loading
  const effectiveLoading = batchedMode ? !!externalLoading : loading;
  if (effectiveLoading) {
    return (
      <div
        className={`rounded-xl border shadow-sm p-6 ${className}`}
        style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
          </div>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Industry Benchmarks</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
          <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Loading benchmark data...</span>
        </div>
      </div>
    );
  }

  // Error / no data
  if (error || !metrics) {
    return (
      <div
        className={`rounded-xl border shadow-sm p-6 ${className}`}
        style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
            </div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Industry Benchmarks</h3>
          </div>
          <button
            onClick={() => reportId && fetchMetrics(reportId)}
            className="p-1.5 transition-colors rounded"
            style={{ color: 'var(--color-text-dim)' }}
            title="Retry"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <div className="text-center py-6">
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{error || 'No benchmark data available'}</p>
        </div>
      </div>
    );
  }

  // Render with data
  return (
    <div className={className}>
      <IndustryBenchmark
        businessType={metrics.businessType}
        naicsCode={metrics.naicsCode}
        cogsRatio={metrics.cogsRatio}
        grossMargin={metrics.grossMargin}
        operatingExpenseRatio={metrics.operatingExpenseRatio}
        netProfitMargin={metrics.netProfitMargin}
        hasReconstructedData={metrics.hasReconstructedData}
      />
    </div>
  );
}
